import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Box, Flex, Text, Button, IconButton, Badge, Spinner, useToast, Image,
  Grid, Input, Textarea, Select, Checkbox, Menu, MenuButton, MenuList, MenuItem,
} from "@chakra-ui/react";
import { DeleteIcon, ChevronDownIcon } from "@chakra-ui/icons";
import axiosInstance from "../../utils/axiosInstance";
import { fmtDate, today, Th, Td, timeNow, upper, fmtWeight, PROCESSING_TYPES, SheetField, sheetInputProps, SectionBar } from "./shared";
import printRegistrationForm from "./printRegistrationForm";
import BoxWeightLink from "./BoxWeightLink";
import ProcessingReportLink from "./ProcessingReportLink";
import LotPicker from "../../components/LotPicker";
import VendorInput from "../../components/VendorInput";
import LotTimeline from "../../components/LotTimeline";
import ntiLogo from "../../assets/nti.jpg";
import FloatingWindow from "../../components/FloatingWindow";
import AllFormsTable from "./AllFormsTable";
import SearchBar from "../../components/SearchBar";


// Stored verbatim as the field value, so the number, abbreviation and name all
// survive into the printed form and the history snapshot with no lookup table
// to keep in sync.

const emptyDraft = () => ({
  id: null,
  lotNumber: "", lotId: null, pendingBatchIds: [],
  // Product is registered when it arrives, so "now" is right far more often
  // than blank — for the form's own date as much as the received date. All
  // stay editable, and an existing form always loads its own stored values
  // over these, so opening an old form cannot backfill today onto it.
  formDate: today(), dateReceived: today(), timeReceived: timeNow(),
  vendorLot: "", vendor: "", productDescription: "", processingType: "",
  spec: "", brand: "", estNumber: "", grade: "",
  dueDate: "", predictedYield: "",
  manifestBlAttached: false, processReportAttached: false,
  originalWeight: "", totalQuantity: "",
  processingDates: [
    { date: "", weight: "", cases: "" },
  ],
  actualYield: "", temp: "", remarks: "", checkedBy: "",
  status: "in_progress",
});

// Fills the form with realistic placeholder values — for previewing/printing
// the layout without needing a saved record from the backend.
const sampleDraft = () => ({
  lotNumber: "N26124-01", formDate: today(), dateReceived: today(), timeReceived: "14:30",
  vendorLot: "IC-88213", vendor: "IBP Foods",
  productDescription: "Beef Brisket, Boneless", processingType: "104 CUT-PK 1/2 Cutting & Packing",
  spec: "10X30", brand: "IBP", estNumber: "9268", grade: "Choice",
  dueDate: today(), predictedYield: "92",
  manifestBlAttached: true, processReportAttached: false,
  originalWeight: "1842", totalQuantity: "24",
  processingDates: [
    { date: today(), weight: "1695", cases: "22" },
  ],
  actualYield: "92.0", temp: "27",
  remarks: "Sample record for print preview — no backend data behind this.",
  checkedBy: "J. Rivera",
});

const RegistrationFormModal = ({ isOpen, onClose, draft, setDraft, onSave, saving }) => {
  // Accepted reports are what the locked run rows below say; the panel needs
  // the waiting ones too, so this loads both and hands them down.
  const [reports, setReports] = useState([]);
  const lotId = draft && draft.lotId;
  const lotNumber = draft && draft.lotNumber;

  const loadReports = useCallback(async () => {
    // The number is enough when there is no id: a form for an external lot, or
    // one saved before lot_id existed, still knows what lot it is.
    if (!lotId && !lotNumber) { setReports([]); return; }
    try {
      const { data } = await axiosInstance.get("/processing-reports", {
        params: lotId ? { lotId } : { lotNumber },
      });
      setReports(data || []);
    } catch {
      // The form still has to be fillable with the reports unreachable.
    }
  }, [lotId, lotNumber]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const reportById = useMemo(
    () => new Map(reports.map((r) => [r.reportId, r])), [reports]
  );

  const [isFullScreen, setIsFullScreen] = useState(false);
  if (!draft) return null;
  const set = (key) => (e) => setDraft({ ...draft, [key]: upper(e.target.value) });
  const setCheck = (key) => (e) => setDraft({ ...draft, [key]: e.target.checked });
  const tally = caseTally(draft);
  const remainingCases = tally ? tally.remaining : null;

  // Every case arrived is accounted for — offer to close the form, do not close
  // it. Three things have to hold, and each rules out a way this would be wrong:
  //
  //   tally            total_quantity parsed to a number. It is free text, and
  //                    parseFloat takes a LEADING number — "24 cases" is 24, so
  //                    that form works. It is "TBD" or "twenty four", with no
  //                    leading digits, that yields nothing and stays silent.
  //   processed > 0    nothing has been done yet is ALSO a remainder of zero
  //                    (a blank form where total is "0"), and that is not done.
  //   not completed    nothing to suggest if it already is.
  //
  // Suggested rather than applied because completing is what drops a form out
  // of the default In Progress list, and this recomputes on every keystroke —
  // correcting 12 to 2 to 22 passes through zero on the way.
  const suggestComplete =
    tally !== null && tally.remaining === 0 && tally.processed > 0
    && draft.status !== "completed";

  return (
    <FloatingWindow
      isOpen={isOpen}
      onClose={onClose}
      title={draft.id ? `Edit Registration Form${draft.lotNumber ? " — " + draft.lotNumber : ""}` : "New Registration Form"}
      isFullScreen={isFullScreen}
      onToggleFullScreen={() => setIsFullScreen((v) => !v)}
      width={900}
      footer={<Flex justify="space-between" width="100%" gap={2} wrap="wrap">
        <Button size="sm" variant="outline" colorScheme="teal" onClick={() => setDraft({ ...draft, ...sampleDraft() })}>
          Fill Sample Data
        </Button>
        <Flex gap={2} flex={{ base: "1 1 100%", md: "0 0 auto" }} justify="flex-end">
          <Button size="sm" variant="outline" onClick={() => printRegistrationForm(draft)}>Print</Button>
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button size="sm" colorScheme="blue" isLoading={saving} onClick={onSave}>Save</Button>
        </Flex>
      </Flex>}
    >
        <Box maxW="820px" mx="auto">
          <Flex
            direction={{ base: "column", md: "row" }}
            align={{ base: "stretch", md: "flex-start" }}
            justify="space-between" gap={{ base: 3, md: 0 }}
            borderBottom="2px solid #2b6cb0" pb={2} mb={3}
          >
            <Flex align="center" gap={3} minW={0}>
              <Image
                src={ntiLogo} alt="Noblesse Trading Inc."
                width={{ base: "130px", md: "200px" }} objectFit="contain"
                flexShrink={0}
              />
              {draft.lotNumber && (
                <Box borderLeft="2px solid" borderColor="blue.200" pl={3} minW={0}>
                  <Text fontSize="10px" color="gray.500" textTransform="uppercase"
                    letterSpacing="wide" lineHeight="1">
                    Lot
                  </Text>
                  <Text fontSize={{ base: "md", md: "xl" }} fontWeight="bold"
                    color="blue.800" lineHeight="1.2" whiteSpace="nowrap">
                    {draft.lotNumber}
                  </Text>
                </Box>
              )}
            </Flex>
            <Flex direction="column" gap={2} align={{ base: "stretch", md: "flex-end" }}>
              <Flex align="center" gap={2}>
                <Text fontSize="sm" fontWeight="bold" color="blue.700" minW="40px">
                  {draft.lotNumber ? "Change:" : "Lot#:"}
                </Text>
                {/* A registration form is an incoming-side record, so it may
                    issue a lot. Picking one here is also what makes the box
                    weights below findable — they are matched by lot.

                    hideRegistered because this IS the screen that registers a
                    lot: one that already has a form is not a candidate, and it
                    made up three quarters of the list. The form's own lot is
                    kept so the select stays bound to it, and it is read off the
                    masthead above rather than out of this control. */}
                <Box width={{ base: "100%", md: "260px" }}>
                  <LotPicker
                    allowCreate
                    hideRegistered
                    value={draft.lotId ?? null}
                    lotNumber={draft.lotNumber || ""}
                    onChange={(lot) => setDraft({
                      ...draft,
                      lotId: lot ? lot.lotId : null,
                      lotNumber: lot ? lot.lotNumber : "",
                    })}
                  />
                </Box>
              </Flex>
              <Flex align="center" gap={2}>
                <Text fontSize="sm" fontWeight="bold" color="blue.700" minW="40px">Date:</Text>
                <Input size="sm" width={{ base: "100%", md: "150px" }} type="date" value={draft.formDate} onChange={set("formDate")} />
              </Flex>
            </Flex>
          </Flex>
          <Text textAlign="center" fontSize="lg" fontWeight="extrabold" color="blue.700" mb={4}>
            {draft.id ? "Edit Registration Form" : "New Registration Form"}
          </Text>

          <Grid
            templateColumns={{ base: "1fr", md: "1fr 2fr 1fr 2fr" }}
            gap="1px" bg="gray.200" mb={5} border="1px solid" borderColor="gray.200"
          >
            <SectionBar>Logistics &amp; Vendor</SectionBar>
            <SheetField label="Date Received"><Input {...sheetInputProps} type="date" value={draft.dateReceived} onChange={set("dateReceived")} /></SheetField>
            <SheetField label="Time Received"><Input {...sheetInputProps} type="time" value={draft.timeReceived} onChange={set("timeReceived")} /></SheetField>
            <SheetField label="Vendor Lot/(IC)#"><Input {...sheetInputProps} value={draft.vendorLot} onChange={set("vendorLot")} /></SheetField>
            {/* Suggests spellings already in use so one company does not end up
                as four vendors. Still free text — a new supplier gets typed. */}
            <SheetField label="Vendor">
              <VendorInput {...sheetInputProps} value={draft.vendor} onChange={set("vendor")}
                listId="vendor-suggestions-form" />
            </SheetField>

            <SectionBar>Product Identification</SectionBar>
            <SheetField label="Product Description" full><Input {...sheetInputProps} value={draft.productDescription} onChange={set("productDescription")} /></SheetField>
            <SheetField label="Processing Type" full>
              {/* Styled like the Status select rather than with sheetInputProps,
                  which strips the border and makes a dropdown look like a
                  plain text field. */}
              <Select size="sm" bg="white" border="1px solid" borderColor="gray.300"
                value={draft.processingType || ""} onChange={set("processingType")}>
                <option value="">— Select processing type —</option>
                {PROCESSING_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                {/* Forms saved before this list existed hold free text. Keeping
                    the current value as an option stops opening an old form
                    from silently blanking the field. */}
                {draft.processingType && !PROCESSING_TYPES.includes(draft.processingType) && (
                  <option value={draft.processingType}>{draft.processingType} (existing)</option>
                )}
              </Select>
            </SheetField>
            <SheetField label="Original Wt. (lbs)"><Input {...sheetInputProps} type="number" value={draft.originalWeight} onChange={set("originalWeight")} /></SheetField>
            <SheetField label="Total Quantity (c/s)"><Input {...sheetInputProps} value={draft.totalQuantity} onChange={set("totalQuantity")} /></SheetField>
            <SheetField label="Spec. ">
              <Flex gap={1} align="center">
                <Input
                  {...sheetInputProps}
                  placeholder="10"
                  value={draft.spec ? draft.spec.split("X")[0] || "" : ""}
                  onChange={(e) => {
                    const parts = draft.spec ? draft.spec.split("X") : ["", ""];
                    setDraft({ ...draft, spec: upper(`${e.target.value}X${parts[1] || ""}`.replace(/^X/, "")) });
                  }}
                  flex={1}
                />
                <Text fontSize="sm" fontWeight="bold" color="gray.600">×</Text>
                <Input
                  {...sheetInputProps}
                  placeholder="30"
                  value={draft.spec ? draft.spec.split("X")[1] || "" : ""}
                  onChange={(e) => {
                    const parts = draft.spec ? draft.spec.split("X") : ["", ""];
                    setDraft({ ...draft, spec: upper(`${parts[0] || ""}X${e.target.value}`.replace(/X$/, "")) });
                  }}
                  flex={1}
                />
              </Flex>
            </SheetField>
            <SheetField label="Brand"><Input {...sheetInputProps} value={draft.brand} onChange={set("brand")} /></SheetField>
            <SheetField label="EST#"><Input {...sheetInputProps} value={draft.estNumber} onChange={set("estNumber")} /></SheetField>
            <SheetField label="Grade"><Input {...sheetInputProps} value={draft.grade} onChange={set("grade")} /></SheetField>

            <SectionBar>Estimation/Checks</SectionBar>
            <SheetField label="Due Date?"><Input {...sheetInputProps} type="date" value={draft.dueDate} onChange={set("dueDate")} /></SheetField>
            <SheetField label="Predicted Yield (%)"><Input {...sheetInputProps} type="number" value={draft.predictedYield} onChange={set("predictedYield")} /></SheetField>
            <SheetField label="Manifest/BL Attached?" plain>
              <Checkbox isChecked={draft.manifestBlAttached} onChange={setCheck("manifestBlAttached")} px={2} />
            </SheetField>
            <SheetField label="Process Report Attached?" plain>
              <Checkbox isChecked={draft.processReportAttached} onChange={setCheck("processReportAttached")} px={2} />
            </SheetField>

            {/* Where Original Weight and Total Quantity actually come from —
                both live in Product Identification above, since they describe
                what arrived rather than what processing did to it. */}
            <SectionBar>Box Weights</SectionBar>
            <Box gridColumn="1 / -1" p={2}>
              <BoxWeightLink
                formId={draft.id}
                lotNumber={draft.lotNumber}
                draft={draft}
                setDraft={setDraft}
                pendingBatchIds={draft.pendingBatchIds || []}
                onPendingChange={(ids) => setDraft({ ...draft, pendingBatchIds: ids })}
              />
            </Box>

            <SectionBar>Processing &amp; Yield</SectionBar>

            {/* Reports from the floor. Accepting one takes its cases off the
                lot and adds a row below, so it saves straight away rather than
                waiting for the form to be saved. */}
            <Box gridColumn="1 / -1" px={3} py={3} bg="gray.50"
              borderBottom="1px solid" borderColor="gray.200">
              <Text fontSize="xs" color="gray.500" textTransform="uppercase"
                letterSpacing="wide" mb={2}>
                Processing reports
              </Text>
              <ProcessingReportLink
                lotId={draft.lotId}
                lotNumber={draft.lotNumber}
                reports={reports}
                onApplied={async () => {
                  await loadReports();
                  // The accept appended a run row server-side, so the draft in
                  // hand is behind. Reloaded rather than guessed at.
                  if (!draft.id) return;
                  const { data } = await axiosInstance.get(`/noblesse-registration-forms/${draft.id}`);
                  setDraft((d) => ({ ...d, processingDates: data.processingDates || d.processingDates }));
                }}
              />
            </Box>

            {Array.isArray(draft.processingDates) && draft.processingDates.map((pd, idx) => {
              // A row carrying a reportId was written by an accepted report, so
              // it is shown rather than edited — retyping it by hand would make
              // the form disagree with the report that moved the stock. An
              // admin takes it back with Un-accept, not by deleting a row.
              const from = pd.reportId != null ? reportById.get(pd.reportId) : null;
              if (pd.reportId != null) {
                return (
                  <SheetField key={idx} label={`(${idx + 1}) Processed`} full plain>
                    <Box px={3} py={2} bg="green.50" borderRadius="sm">
                      <Flex align="baseline" gap={3} wrap="wrap">
                        <Text fontSize="sm" fontWeight="bold" color="green.900"
                          style={{ fontVariantNumeric: "tabular-nums" }}>
                          {pd.cases != null ? `${pd.cases} cases` : "—"}
                        </Text>
                        {pd.weight ? (
                          <Text fontSize="sm" color="gray.700"
                            style={{ fontVariantNumeric: "tabular-nums" }}>
                            {pd.weight} lb
                          </Text>
                        ) : null}
                        <Text fontSize="xs" color="gray.600">{fmtDate(pd.date) || "—"}</Text>
                        {from && from.lineNo && (
                          <Badge colorScheme="gray" fontSize="9px">Line {from.lineNo}</Badge>
                        )}
                        <Box flex={1} />
                        <Badge colorScheme="green" fontSize="9px">from report</Badge>
                      </Flex>
                      {from && (
                        <Flex align="baseline" gap={3} wrap="wrap" mt={1}>
                          {from.processingType && (
                            <Text fontSize="xs" color="gray.600">{from.processingType}</Text>
                          )}
                          {from.workers && from.workers.length > 0 && (
                            <Text fontSize="xs" color="gray.600">{from.workers.join(", ")}</Text>
                          )}
                          {from.inedibleWeight && (
                            <Text fontSize="xs" color="gray.600">
                              {from.inedibleWeight} lb inedible
                            </Text>
                          )}
                          {from.acceptedBy && (
                            <Text fontSize="xs" color="gray.500">
                              accepted by {from.acceptedBy}
                            </Text>
                          )}
                        </Flex>
                      )}
                    </Box>
                  </SheetField>
                );
              }
              return (
              <React.Fragment key={idx}>
                {/* One processing event per row: weight, cases and the date it
                    happened. The date carries no label of its own — a date
                    input is self-evident, and the row reads left to right. */}
                <SheetField label={`(${idx + 1}) Processed`} full>
                  {/* Three fields share one cell, so they are white and spaced:
                      the cell's own grey shows through as a gutter and the row
                      reads as weight / cases / date rather than one long strip.
                      Every other field on the sheet is a lone input filling its
                      cell, which is why only this row needs it. */}
                  <Flex gap={5} align="center" px={3} py={1.5}>
                    <Input
                      {...sheetInputProps}
                      bg="white"
                      size="sm"
                      flex="1 1 30%"
                      type="number"
                      placeholder="lbs"
                      title="Weight taken from inventory and processed (lbs) — not what came out"
                      value={pd.weight || ""}
                      onChange={(e) => {
                        const newDates = [...draft.processingDates];
                        newDates[idx] = { ...newDates[idx], weight: upper(e.target.value) };
                        setDraft({ ...draft, processingDates: newDates });
                      }}
                    />
                    <Input
                      {...sheetInputProps}
                      bg="white"
                      size="sm"
                      flex="1 1 30%"
                      type="number"
                      placeholder="c/s"
                      title="Cases taken from inventory and processed — not what came out"
                      value={pd.cases || ""}
                      onChange={(e) => {
                        const newDates = [...draft.processingDates];
                        newDates[idx] = { ...newDates[idx], cases: upper(e.target.value) };
                        setDraft({ ...draft, processingDates: newDates });
                      }}
                    />
                    <Input
                      {...sheetInputProps}
                      bg="white"
                      size="sm"
                      flex="1 1 30%" minW="118px"
                      type="date"
                      title="Processing date"
                      value={pd.date || ""}
                      onChange={(e) => {
                        const newDates = [...draft.processingDates];
                        newDates[idx] = { ...newDates[idx], date: upper(e.target.value) };
                        setDraft({ ...draft, processingDates: newDates });
                      }}
                    />
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      minW="auto"
                      px={1}
                      ml={1}
                      flexShrink={0}
                      // A row from an accepted report is never hand-deleted:
                      // its cases are off the lot, and removing the row here
                      // would leave the stock moved with nothing explaining it.
                      // Un-accept is the way back, and it restores both.
                      isDisabled={pd.reportId != null || draft.processingDates.length <= 1}
                      title={pd.reportId != null
                        ? "From a processing report — un-accept it instead"
                        : draft.processingDates.length <= 1
                          ? "Cannot delete last row"
                          : "Delete this row"}
                      onClick={() => {
                        if (pd.reportId != null) return;
                        const newDates = draft.processingDates.filter((_, i) => i !== idx);
                        setDraft({ ...draft, processingDates: newDates });
                      }}
                    >
                      ×
                    </Button>
                    {idx === draft.processingDates.length - 1 && (
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="blue"
                        minW="auto"
                        px={2}
                        flexShrink={0}
                        title="Add another processing date"
                        onClick={() => {
                          const newDates = Array.isArray(draft.processingDates) ? [...draft.processingDates] : [];
                          newDates.push({ date: "", weight: "", cases: "" });
                          setDraft({ ...draft, processingDates: newDates });
                        }}
                      >
                        +
                      </Button>
                    )}
                  </Flex>
                </SheetField>
              </React.Fragment>
              );
            })}

            {/* Was labelled "Actual Yield (%)", which it never was: a
                processing row records inventory CONSUMED, so this is how far
                through the lot we are, not what the process returned. */}
            <SheetField label="Processed (%)">
              <Box {...sheetInputProps} bg="white" border="1px solid" borderColor="gray.200" py={1}>
                <Flex align="baseline" gap={2} wrap="wrap">
                  <Text fontSize="sm" color="gray.700" fontWeight="medium">
                    {processedPercent(draft) ?? "—"}
                  </Text>
                  <Text fontSize="10px" color="gray.400">of what arrived</Text>
                </Flex>
              </Box>
            </SheetField>
            <SheetField label="Remaining (c/s)">
              <Box {...sheetInputProps} bg="white" border="1px solid" borderColor="gray.200" py={1}>
                <Flex align="center" gap={2} wrap="wrap">
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color={remainingCases !== null && remainingCases < 0 ? "red.600" : "gray.700"}
                  >
                    {remainingCases === null ? "—" : remainingCases}
                  </Text>
                  {suggestComplete && (
                    <Button size="xs" colorScheme="green" variant="outline"
                      height="20px" fontSize="10px" px={4}
                      title="All cases arrived have been processed"
                      onClick={() => setDraft({ ...draft, status: "completed" })}>
                      Mark complete
                    </Button>
                  )}
                </Flex>
              </Box>
            </SheetField>

            <SheetField label="Temp"><Input {...sheetInputProps} value={draft.temp} onChange={set("temp")} /></SheetField>
            <Box gridColumn="3 / -1" />

            <SectionBar>Additional</SectionBar>
            <SheetField label="Remarks" full>
              <Textarea {...sheetInputProps} rows={3} value={draft.remarks} onChange={set("remarks")} />
            </SheetField>
            <SheetField label="Checked By"><Input {...sheetInputProps} value={draft.checkedBy} onChange={set("checkedBy")} /></SheetField>
            <SheetField label="Status" plain>
              <Select size="sm" bg="white" border="1px solid" borderColor="gray.300" value={draft.status} onChange={set("status")}>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </Select>
            </SheetField>
          </Grid>
        </Box>
    </FloatingWindow>
  );
};

// Human labels for the snapshot shown on create/delete history entries.
const SNAPSHOT_FIELDS = [
  ["lotNumber", "Lot #"],
  ["formDate", "Form date"],
  ["dateReceived", "Date received"],
  ["timeReceived", "Time received"],
  ["vendor", "Vendor"],
  ["vendorLot", "Vendor lot"],
  ["productDescription", "Product"],
  ["processingType", "Processing type"],
  ["spec", "Spec"],
  ["brand", "Brand"],
  ["estNumber", "EST #"],
  ["grade", "Grade"],
  ["dueDate", "Due date"],
  ["predictedYield", "Predicted yield"],
  ["originalWeight", "Original weight"],
  ["totalQuantity", "Total quantity"],
  ["actualYield", "Actual yield"],
  ["temp", "Temp"],
  ["remarks", "Remarks"],
  ["checkedBy", "Checked by"],
  ["status", "Status"],
];

const isBlank = (v) =>
  v === null || v === undefined || v === "" ||
  (Array.isArray(v) && v.length === 0);

// Renders the full form captured on a create or a delete. Empty fields are
// omitted so a sparse form does not produce a wall of dashes.
const HistorySnapshot = ({ form, tone, label }) => {
  if (!form) return null;
  const filled = SNAPSHOT_FIELDS.filter(([key]) => !isBlank(form[key]));
  const dates = Array.isArray(form.processingDates)
    ? form.processingDates.filter((pd) => pd && (pd.date || pd.weight))
    : [];

  if (filled.length === 0 && dates.length === 0) {
    return <Text fontSize="xs" color="gray.500" fontStyle="italic">No field data recorded.</Text>;
  }

  return (
    <Box mt={1} p={2} bg={`${tone}.50`} borderRadius="md" border="1px solid" borderColor={`${tone}.200`}>
      <Text fontSize="xs" fontWeight="bold" color={`${tone}.800`} mb={1}>{label}</Text>
      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={1} fontSize="xs">
        {filled.map(([key, labelText]) => (
          <Flex key={key} gap={2}>
            <Text color="gray.500" minW="110px" flexShrink={0}>{labelText}:</Text>
            <Text color="gray.800" wordBreak="break-word">{String(form[key])}</Text>
          </Flex>
        ))}
      </Grid>
      {dates.length > 0 && (
        <Box mt={1}>
          <Text color="gray.500" fontSize="xs">Processing:</Text>
          {dates.map((pd, i) => (
            <Text key={i} fontSize="xs" color="gray.800" pl={2}>
              ({i + 1}) {pd.date || "—"} · {pd.weight ?? "—"} lbs
            </Text>
          ))}
        </Box>
      )}
    </Box>
  );
};

const statusColor = (status) => (status === "completed" ? "green" : "yellow");

// How much of the lot has been PUT THROUGH processing, as a percentage of what
// arrived.
//
// THIS IS NOT A YIELD, and it was labelled as one. A processing row records how
// much of our existing inventory was consumed — not what came out the other
// side. So weight-processed over weight-received is a measure of PROGRESS
// through the lot, and it can only ever approach 100%.
//
// A real yield is finished product out over raw material in, and this form has
// no way to know the "out" side: the whole reason outgoing boxes get weighed
// separately is that processing output cannot be captured as it happens. The
// paper form's own "Actual Yield" box stays for that number, filled once it is
// known — which is why it is not wired to this figure.
//
// The same reading is already baked into caseTally, where remaining = arrived
// less processed. The two agree now.
const processedPercent = (draft) => {
  if (!draft || !draft.originalWeight || !Array.isArray(draft.processingDates)) {
    return null;
  }
  const originalWeight = parseFloat(draft.originalWeight);
  if (!originalWeight || originalWeight <= 0) return null;

  const consumed = draft.processingDates.reduce((sum, pd) => {
    const weight = parseFloat(pd.weight || 0);
    return sum + (isNaN(weight) ? 0 : weight);
  }, 0);

  if (consumed === 0) return null;
  return (consumed / originalWeight * 100).toFixed(2);
};

// Cases still to process: what arrived, less every case processed so far.
//
// total_quantity is a free-text column and sometimes carries wording rather
// than a bare number, so anything that will not parse yields no figure at all
// rather than a confidently wrong one.
// Returns both halves, not just the difference: "nothing left to do" and
// "nothing done yet" both read as a remainder of zero, and only the processed
// figure tells them apart.
// A week of intake per page. Counted in days rather than rows so one busy
// Tuesday does not split a week across two pages.
const DAYS_PER_PAGE = 7;

const caseTally = (draft) => {
  if (!draft) return null;
  const total = parseFloat(draft.totalQuantity);
  if (isNaN(total)) return null;

  const processed = (Array.isArray(draft.processingDates) ? draft.processingDates : [])
    .reduce((sum, pd) => {
      const n = parseFloat(pd.cases);
      return sum + (isNaN(n) ? 0 : n);
    }, 0);

  return { total, processed, remaining: total - processed };
};

export const RegistrationFormTab = ({ isAdmin, canDelete = false, isAdminUser = false, refreshSignal = 0 }) => {
  // Which lot's history is open, or null. Keyed by the form's lot_id rather
  // than its lot text, so it cannot land on the wrong lot.
  const [timelineLot, setTimelineLot] = useState(null);
  const toast = useToast();
  const [forms, setForms]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft]     = useState(null);
  const [saving, setSaving]   = useState(false);
  // All, not just in progress: a completed form is still the thing someone
  // opens to check what a lot was registered as.
  const [statusFilter, setStatusFilter] = useState(null);
  const [page, setPage] = useState(0);
  const [allHistoryOpen, setAllHistoryOpen] = useState(false);
  const [allHistory, setAllHistory] = useState([]);
  const [allHistoryLoading, setAllHistoryLoading] = useState(false);
  const [excelViewOpen, setExcelViewOpen] = useState(false);
  // A search is held apart from `forms` rather than replacing it: View All
  // reads `forms`, and it should still show every form while a search is up.
  // Run on the server, so it finds forms past the newest 200.
  const [q, setQ] = useState("");
  const [found, setFound] = useState(null);   // null = not searching
  const [searching, setSearching] = useState(false);
  const searchSeq = useRef(0);

  const fetchForms = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/noblesse-registration-forms");
      setForms(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFound = useCallback(async () => {
    const mine = ++searchSeq.current;
    if (!q) { setFound(null); setSearching(false); return; }
    try {
      const res = await axiosInstance.get("/noblesse-registration-forms", { params: { q } });
      // A reply to an older search is dropped rather than shown over a newer one.
      if (mine === searchSeq.current) setFound(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      if (mine === searchSeq.current) setSearching(false);
    }
  }, [q]);

  // Edits land in both lists, so a form changed mid-search is right in the
  // results AND in View All.
  const updateForms = (fn) => {
    setForms(fn);
    setFound((prev) => (prev ? fn(prev) : prev));
  };

  const openAllHistoryModal = async () => {
    setAllHistoryOpen(true);
    setAllHistoryLoading(true);
    try {
      const res = await axiosInstance.get("/noblesse-registration-forms/all/history");
      setAllHistory(res.data || []);
    } catch (err) {
      console.error(err);
      toast({ status: "error", title: "Failed to load history", duration: 2000 });
    } finally {
      setAllHistoryLoading(false);
    }
  };

  const closeAllHistoryModal = () => setAllHistoryOpen(false);

  useEffect(() => { fetchForms(); }, [fetchForms]);
  useEffect(() => { fetchFound(); }, [fetchFound]);

  // Re-fetch when the parent's auto-refresh ticks. Compared against a ref so
  // the initial value does not trigger a duplicate fetch alongside the mount
  // effect above.
  const lastSignal = useRef(refreshSignal);
  useEffect(() => {
    if (lastSignal.current === refreshSignal) return;
    lastSignal.current = refreshSignal;
    fetchForms();
    fetchFound();
  }, [refreshSignal, fetchForms, fetchFound]);

  const openNew  = () => setDraft(emptyDraft());
  const openEdit = (form) => {
    const draft = { ...emptyDraft(), ...form };
    // Convert backend format to array format. An empty array is truthy, so it
    // has to be checked for explicitly — otherwise the form renders with no
    // processing row at all and the yield can never be calculated.
    if (!Array.isArray(draft.processingDates) || draft.processingDates.length === 0) {
      const dates = [];
      for (let i = 1; i <= 10; i++) {
        if (draft[`processingDate${i}`] || draft[`processedWeight${i}`]) {
          dates.push({ date: draft[`processingDate${i}`] || "", weight: draft[`processedWeight${i}`] || "" });
        }
      }
      if (dates.length === 0) dates.push({ date: "", weight: "" });
      draft.processingDates = dates;
    }
    setDraft(draft);
  };
  const close    = () => setDraft(null);

  const save = async () => {
    setSaving(true);
    try {
      // Send processingDates array directly to backend - no conversion needed
      const payload = { ...draft };

      if (draft.id) {
        const res = await axiosInstance.patch(`/noblesse-registration-forms/${draft.id}`, payload);
        updateForms((prev) => prev.map((f) => (f.id === res.data.id ? res.data : f)));
      } else {
        const res = await axiosInstance.post("/noblesse-registration-forms", payload);

        // The boxes are weighed before the form is written up, so sessions can
        // be picked on a form that does not exist yet. They are held on the
        // draft and tied here, the moment there is an id to tie them to.
        const pending = draft.pendingBatchIds || [];
        if (pending.length) {
          try {
            await axiosInstance.post(
              `/noblesse-registration-forms/${res.data.id}/box-batches`,
              { batchIds: pending }
            );
          } catch (err) {
            // The form itself saved. Say what did not, rather than letting the
            // whole save read as a failure.
            toast({
              status: "warning", duration: 6000, isClosable: true,
              title: "Form saved, but the weighing sessions were not tied",
              description: err.response?.data?.error || err.message,
            });
          }
        }
        // Into the search results too, so what was just made is on screen.
        updateForms((prev) => [res.data, ...prev]);
      }
      toast({ status: "success", title: "Registration form saved", duration: 2000 });
      close();
    } catch (err) {
      toast({ status: "error", title: "Save failed", description: err.message, duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this registration form?")) return;
    try {
      await axiosInstance.delete(`/noblesse-registration-forms/${id}`);
      updateForms((prev) => prev.filter((f) => f.id !== id));
    } catch (err) {
      toast({ status: "error", title: "Delete failed", description: err.message, duration: 3000 });
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const res = await axiosInstance.patch(`/noblesse-registration-forms/${id}/status`, { status: newStatus });
      updateForms((prev) => prev.map((f) => (f.id === id ? res.data : f)));
      toast({
        status: "success",
        title: `Status changed to ${newStatus === "completed" ? "Completed" : "In Progress"}`,
        duration: 2000,
      });
    } catch (err) {
      toast({ status: "error", title: "Failed to update status", description: err.message, duration: 3000 });
    }
  };

  if (loading) {
    return <Flex justify="center" py={10}><Spinner size="lg" color="blue.500" /></Flex>;
  }

  // Newest lot first, matching the weight-manifest list so the two tabs read
  // the same way round.
  //
  // N{YY}{JJJ}-{NN} is fixed width, so comparing the TEXT sorts by year, then
  // Julian day, then sequence in one comparison — nothing is parsed, so nothing
  // can be parsed wrong (CLAUDE.md §3). Upper-cased and trimmed so casing or a
  // stray space cannot split one lot across two positions, and a form with no
  // lot yet sorts LAST rather than riding above today's work.
  const matching = (found ?? forms)
    .filter((f) => !statusFilter || f.status === statusFilter)
    .slice()
    .sort((a, b) => {
      const la = String(a.lotNumber || "").trim().toUpperCase();
      const lb = String(b.lotNumber || "").trim().toUpperCase();
      if (la !== lb) return lb.localeCompare(la);
      // Same lot — two forms against one lot happens. Newest first.
      return String(b.dateReceived || "").localeCompare(String(a.dateReceived || ""));
    });

  // Every distinct day, newest first. A form with no date sorts last and rides
  // on the oldest page rather than vanishing.
  const days = [...new Set(matching.map((f) => f.dateReceived || ""))]
    .sort((a, b) => (a && b ? b.localeCompare(a) : a ? -1 : 1));
  const pageCount = Math.max(1, Math.ceil(days.length / DAYS_PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const pageDays = new Set(days.slice(safePage * DAYS_PER_PAGE, (safePage + 1) * DAYS_PER_PAGE));
  const filteredForms = matching.filter((f) => pageDays.has(f.dateReceived || ""));

  const pageLabel = (() => {
    const shown = [...pageDays].filter(Boolean).sort();
    if (!shown.length) return "No date";
    const first = fmtDate(shown[0]);
    const last = fmtDate(shown[shown.length - 1]);
    return first === last ? first : `${first} – ${last}`;
  })();

  // Pacific business date, never the browser's local one — see shared.jsx.
  const todayStr = today();

  return (
    <Box>
      <Flex justify="space-between" align={{ base: "stretch", md: "center" }} mb={4}
        direction={{ base: "column", md: "row" }} gap={{ base: 3, md: 0 }}>
        <Flex align="center" gap={3} flex={1} wrap="wrap">
          <Text fontSize="sm" fontWeight="semibold" color="gray.600" textTransform="uppercase" letterSpacing="wide">
            Registration Forms
          </Text>
          <SearchBar
            placeholder="Search lot, vendor, product…"
            isSearching={searching}
            onSearch={(text) => { setSearching(true); setQ(text); setPage(0); }}
          />
          <Flex gap={1}>
            <Button
              size="xs"
              variant={statusFilter === null ? "solid" : "outline"}
              colorScheme={statusFilter === null ? "teal" : "gray"}
              onClick={() => { setStatusFilter(null); setPage(0); }}
            >
              All
            </Button>
            <Button
              size="xs"
              variant={statusFilter === "in_progress" ? "solid" : "outline"}
              colorScheme={statusFilter === "in_progress" ? "blue" : "gray"}
              onClick={() => { setStatusFilter("in_progress"); setPage(0); }}
            >
              In Progress
            </Button>
            <Button
              size="xs"
              variant={statusFilter === "completed" ? "solid" : "outline"} 
              colorScheme={statusFilter === "completed" ? "green" : "gray"}
              onClick={() => { setStatusFilter("completed"); setPage(0); }}
            >
              Completed
            </Button>
          </Flex>
        </Flex>
        <Flex gap={2} wrap="wrap">
          <Button size="xs" colorScheme="gray" variant="outline" onClick={() => setExcelViewOpen(true)}>View All</Button>
          {/* The isAdmin prop above is really "can edit" and is always true here.
              History is gated on the actual role check. */}
          {isAdminUser && <Button size="xs" colorScheme="gray" onClick={openAllHistoryModal}>History</Button>}
          {isAdmin && <Button size="xs" colorScheme="blue" onClick={openNew}>+ New Registration Form</Button>}
        </Flex>
      </Flex>

      {filteredForms.length === 0 ? (
        <Text fontSize="sm" color="gray.400">
          {/* A search that finds nothing empties the list too — read the old
              way, that claimed no form had ever been saved. */}
          {q
            ? (found && found.length > 0 && statusFilter
              ? `No ${statusFilter === "in_progress" ? "in progress" : "completed"} form matches “${q}”, but ${found.length} other${found.length === 1 ? " does" : "s do"}.`
              : `Nothing matches “${q}”.`)
            : forms.length === 0 ? "No registration forms saved yet." : statusFilter ? `No ${statusFilter === "in_progress" ? "in progress" : "completed"} registration forms.` : "No registration forms."}
        </Text>
      ) : (
        // A table cannot reflow into a narrow column, so on mobile it scrolls
        // sideways inside this box rather than forcing the whole page to.
        <Box overflowX="auto" width="100%">
        <table style={{ width: "100%", minWidth: "720px", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <Th>Lot #</Th>
              <Th>Date Received</Th>
              <Th>Vendor</Th>
              <Th>Product Description</Th>
              
              <Th textAlign="right">Weight</Th>
              <Th textAlign="right">Rem. Qty</Th>
              
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filteredForms.map((f, i) => {
              // Today's intake in green, matching the weight-manifest list and
              // the All Forms window. This table sets its hover colour
              // imperatively, so the two states are computed together — hovering
              // a green row and leaving it must not repaint it grey.
              const isToday = f.dateReceived === todayStr;
              const rowBg = isToday
                ? "rgb(240, 255, 244)"                                   // green.50
                : i % 2 === 0 ? "white" : "rgb(245, 245, 245)";
              const rowHoverBg = isToday
                ? "rgb(198, 246, 213)"                                   // green.100
                : i % 2 === 0 ? "rgb(245, 245, 245)" : "rgb(230, 230, 230)";
              const remainingCases = caseTally(f)?.remaining;
              return (
              <React.Fragment key={f.id}>
                <tr
                  style={{
                    backgroundColor: rowBg,
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = rowHoverBg; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = rowBg; }}
                  title="Double-click to open"
                  onDoubleClick={() => openEdit(f)}
                >
                  {/* A green left edge makes today's intake scannable down the
                      column without reading a single date. */}
                  <Td fontWeight="medium" color="blue.700"
                    borderLeft={isToday ? "4px solid" : undefined}
                    borderLeftColor={isToday ? "green.500" : undefined}>
                    <Flex align="center" gap={2}>
                      {f.lotId ? (
                        <Text
                          as="span"
                          color="blue.600"
                          fontWeight="600"
                          textDecoration="underline"
                          cursor="pointer"
                          title="Show this lot's history"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTimelineLot({ lotId: f.lotId, lotNumber: f.lotNumber });
                          }}
                          onDoubleClick={(e) => e.stopPropagation()}
                        >
                          {f.lotNumber}
                        </Text>
                      ) : (
                        <Text as="span">{f.lotNumber || "—"}</Text>
                      )}
                    </Flex>
                  </Td>
                  <Td>
                    <Flex align="center" gap={2}>
                      <Text as="span">{fmtDate(f.dateReceived)}</Text>
                      {isToday && (
                        <Badge colorScheme="green" fontSize="9px" px={1.5} borderRadius="full">
                          TODAY
                        </Badge>
                      )}
                    </Flex>
                  </Td>
                  <Td>{f.vendor || "—"}</Td>
                  <Td>{f.productDescription || "—"}</Td>
                  {/* tabular-nums so the decimal points line up down the
                      column; without it proportional digits make the figures
                      ragged and hard to compare at a glance. */}
                  <Td textAlign="right" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {f.originalWeight ? `${fmtWeight(f.originalWeight)} lb` : "—"}
                  </Td>
                  <Td textAlign="right" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {remainingCases || "—"}/{f.totalQuantity || "—"}
                  </Td>
                  <Td>
                    <Menu>
                      <MenuButton
                        as={Badge}
                        colorScheme={statusColor(f.status)}
                        borderRadius="full"
                        px={2}
                        cursor="pointer"
                        _hover={{ opacity: 0.8 }}
                      >
                        <Flex align="center" gap={1}>
                          <Text fontSize="xs">{f.status === "completed" ? "Completed" : "In Progress"}</Text>
                          <ChevronDownIcon boxSize={3} />
                        </Flex>
                      </MenuButton>
                      <MenuList minW="120px">
                        <MenuItem
                          onClick={() => updateStatus(f.id, "in_progress")}
                          isDisabled={f.status === "in_progress"}
                          fontSize="sm"
                        >
                          In Progress
                        </MenuItem>
                        <MenuItem
                          onClick={() => updateStatus(f.id, "completed")}
                          isDisabled={f.status === "completed"}
                          fontSize="sm"
                        >
                          Completed
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                  <Td>
                    <Flex gap={2}>
                      <Button size="xs" variant="outline" onClick={() => openEdit(f)}>
                        {f.status === "completed" ? "View" : "Edit"}
                      </Button>
                      <Button size="xs" variant="outline" colorScheme="blue" onClick={() => printRegistrationForm(f)}>
                        Print
                      </Button>
                      <IconButton size="xs" aria-label="Delete" icon={<DeleteIcon />}
                        variant="ghost" colorScheme="red" onClick={() => remove(f.id)}
                        isDisabled={!canDelete} title={canDelete ? "Delete" : "Admins only"} />
                    </Flex>
                  </Td>
                </tr>
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </Box>
      )}

      {/* Only when there is more than a week of it. A pager over a single page
          is furniture. */}
      {pageCount > 1 && (
        <Flex align="center" justify="space-between" gap={3} wrap="wrap" mt={3}>
          <Text fontSize="xs" color="gray.500">
            {pageLabel} · {filteredForms.length} form{filteredForms.length === 1 ? "" : "s"}
          </Text>
          <Flex align="center" gap={2}>
            <Button size="xs" variant="outline"
              isDisabled={safePage === 0}
              onClick={() => setPage(safePage - 1)}>
              Newer
            </Button>
            <Text fontSize="xs" color="gray.600">
              Week {safePage + 1} of {pageCount}
            </Text>
            <Button size="xs" variant="outline"
              isDisabled={safePage >= pageCount - 1}
              onClick={() => setPage(safePage + 1)}>
              Older
            </Button>
          </Flex>
        </Flex>
      )}

      <FloatingWindow
        isOpen={excelViewOpen}
        onClose={() => setExcelViewOpen(false)}
        title="All Registration Forms"
        width={1360}
      >
        {/* Double-click opens the edit form over this list rather than closing
            it, so the filters and page survive the round trip. */}
        <AllFormsTable forms={forms} onEdit={openEdit} />
      </FloatingWindow>

      <FloatingWindow
        isOpen={allHistoryOpen}
        onClose={closeAllHistoryModal}
        title="Registration Forms — All Changes"
        width={900}
      >
        {allHistoryLoading ? (
          <Flex justify="center" py={6}><Spinner size="sm" color="blue.500" /></Flex>
        ) : allHistory.length === 0 ? (
          <Text fontSize="sm" color="gray.500">No changes recorded</Text>
        ) : (
          <Flex direction="column" gap={3} maxH="70vh" overflowY="auto">
            {allHistory.map((entry, idx) => (
              <Box key={idx} p={3} bg="gray.50" borderRadius="md" borderLeft="4px" borderLeftColor="blue.500">
                <Flex justify="space-between" align="start" gap={2} mb={1}>
                  <Flex gap={2} align="center" flex={1}>
                    <Badge
                      colorScheme={
                        entry.action === "created" ? "green" :
                        entry.action === "deleted" ? "red" :
                        entry.action === "status_changed" ? "teal" :
                        "blue"
                      }
                      fontSize="xs"
                    >
                      {entry.action === "created" && "Created"}
                      {entry.action === "updated" && "Updated"}
                      {entry.action === "status_changed" && "Status"}
                      {entry.action === "deleted" && "Deleted"}
                    </Badge>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700" flex={1}>
                      {entry.lotNumber || "—"}
                    </Text>
                  </Flex>
                  <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
                    {fmtDate(entry.createdAt)}
                  </Text>
                </Flex>
                <Flex gap={2} fontSize="xs" color="gray.600" mb={2}>
                  <Text fontWeight="500">{entry.performedBy || "System"}</Text>
                </Flex>
                {entry.changedFields && entry.changedFields.length > 0 && (
                  <Box>
                    <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1}>
                      Changed: {entry.changedFields.join(", ")}
                    </Text>
                    {entry.oldValues && entry.newValues && (
                      <Flex direction="column" gap={1} fontSize="xs" color="gray.600">
                        {entry.changedFields.map((field) => (
                          <Flex key={field} gap={2}>
                            <Text minW="100px">{field}:</Text>
                            <Text color="red.600">{JSON.stringify(entry.oldValues[field])}</Text>
                            <Text>→</Text>
                            <Text color="green.600">{JSON.stringify(entry.newValues[field])}</Text>
                          </Flex>
                        ))}
                      </Flex>
                    )}
                  </Box>
                )}

                {/* A create or delete has no field diff, so show the whole form
                    instead. For a delete this is the only surviving copy. */}
                {!entry.changedFields?.length && (entry.newValues || entry.oldValues) && (
                  <HistorySnapshot
                    form={entry.newValues || entry.oldValues}
                    tone={entry.action === "deleted" ? "red" : "green"}
                    label={entry.action === "deleted" ? "Deleted record" : "Created with"}
                  />
                )}
              </Box>
            ))}
          </Flex>
        )}
      </FloatingWindow>

      {/* Rendered last on purpose. Every FloatingWindow sits at zIndex 1400, so
          at equal z-index DOM order decides what stacks on top — and the edit
          form has to sit above the list it was opened from. */}
      <LotTimeline
        lotId={timelineLot?.lotId}
        lotNumber={timelineLot?.lotNumber}
        isOpen={Boolean(timelineLot)}
        onClose={() => setTimelineLot(null)}
      />

      <RegistrationFormModal
        isOpen={!!draft} onClose={close}
        draft={draft} setDraft={setDraft}
        onSave={save} saving={saving}
      />
    </Box>
  );
};
